import * as React from 'react';
import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { Button as ButtonV2, Heading } from '@restworld/ui-ds-base';
import { defaultItalyCoordinates } from '@restworld/utils-common';
import { BinocularGirlIllustration } from '@restworld/utils-common/illustrations/binocular-girl-illustration';

import { defaultJobCoordinatesPayload, defaultJobListPayload, defaultExplorerSortFilter } from '../job-explorer.types';
import { isRestaurantsExplorerAtom } from '../job-explorer-map';
import { getMapRefAtom, jobCoordinateParamsAtom } from '../job-explorer-map.atoms';
import { jobListParamsAtom, jobSortFilterAtom } from '../job-explorer-list.atoms';
import { isResetFilterAtom, moreFiltersAtom } from '../job-explorer-filters.atoms';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IDataNotAvailableUIProps {}

const DataNotAvailableUI: React.FunctionComponent<IDataNotAvailableUIProps> = (props) => {
  const { clickHandler } = useDataNotAvailable();
  // useAtomValue, not useAtom: useAtom returns the [value, setter] tuple, which
  // is always truthy, so this flag read true in both explorers and the empty
  // state always said "locali", even when listing positions.
  const isRestaurantsExplorer = useAtomValue(isRestaurantsExplorerAtom);
  const { t } = useTranslation('job-explorer-page');

  return (
    <div className="flex flex-col justify-center items-center gap-4">
      <BinocularGirlIllustration />
      {/* Four whole sentences rather than one with an interpolated noun: a
          translated fragment dropped into a translated frame breaks in any
          language whose article or agreement depends on that noun. */}
      <Heading>
        {t(isRestaurantsExplorer ? 'no_venues_with_applied_filters' : 'no_positions_with_applied_filters')}
      </Heading>
      <Heading size={5} color="muted" weight="normal">
        {t(isRestaurantsExplorer ? 'try_changing_filters_to_find_venues' : 'try_changing_filters_to_find_positions')}
      </Heading>
      <ButtonV2 variant="tertiary" title={t('reset_all_filters')} onClick={clickHandler}>
        {t('reset_all_filters')}
      </ButtonV2>
    </div>
  );
};

export default DataNotAvailableUI;
const useDataNotAvailable = () => {
  const setMoreFilters = useSetAtom(moreFiltersAtom);
  const setIsResetFilter = useSetAtom(isResetFilterAtom);
  const setSortFilter = useSetAtom(jobSortFilterAtom);
  const setJobCoordinateParams = useSetAtom(jobCoordinateParamsAtom);
  const mapRef = useAtomValue(getMapRefAtom);
  const setJobListParams = useSetAtom(jobListParamsAtom);
  const resetCallback = useCallback(() => {
    setJobCoordinateParams(defaultJobCoordinatesPayload);
    setJobListParams(defaultJobListPayload);
    setSortFilter(defaultExplorerSortFilter);
    setMoreFilters({});
    setIsResetFilter(true);
    mapRef.current?.flyTo({
      center: defaultItalyCoordinates,
      zoom: 5,
      duration: 2000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef?.current, setIsResetFilter, setJobCoordinateParams, setJobListParams, setMoreFilters, setSortFilter]);
  const clickHandler = useCallback(() => {
    resetCallback();
  }, [resetCallback]);
  return {
    clickHandler,
  };
};
